import { CSSProperties } from "react";

export type TableFigureState = "free" | "booked" | "closed";

interface Props {
  tableNumber: number;
  capacity: number;
  /** Dolu sandalye sayısı — rezervasyondaki kişi sayısı kadar sandalye dolu çizilir. */
  seated: number;
  state: TableFigureState;
}

const CHAIR = 16;
const GAP = 5;
const STEP = CHAIR + 4;
/** Çok büyük masalarda çizim taşmasın; fazlası "+N" olarak yazılır. */
const MAX_CHAIRS = 16;
/** Bu kapasiteye kadar yuvarlak masa, üstü dikdörtgen masa. */
const ROUND_MAX = 6;

const PALETTE: Record<TableFigureState, { accent: string; surface: string; edge: string }> = {
  free:   { accent: "#16a34a", surface: "#f0fdf4", edge: "#86efac" },
  booked: { accent: "#dc2626", surface: "#fef2f2", edge: "#fca5a5" },
  closed: { accent: "#9ca3af", surface: "#f3f4f6", edge: "#d1d5db" },
};

function Chair({ filled, accent, style }: { filled: boolean; accent: string; style?: CSSProperties }) {
  return (
    <span style={{
      width: CHAIR, height: CHAIR, borderRadius: 5, boxSizing: "border-box", flexShrink: 0,
      border: `1.5px solid ${accent}`, background: filled ? accent : "#fff",
      ...style,
    }} />
  );
}

export default function TableFigure({ tableNumber, capacity, seated, state }: Props) {
  const { accent, surface, edge } = PALETTE[state];
  const chairs = Math.max(1, Math.min(capacity, MAX_CHAIRS));
  const hidden = Math.max(0, capacity - MAX_CHAIRS);
  const filled = (i: number) => i < seated;

  const tableLabel = (
    <span style={{ fontWeight: 800, fontSize: "1rem", color: accent, lineHeight: 1 }}>
      {tableNumber}
      {hidden > 0 && <span style={{ display: "block", fontSize: "0.62rem", fontWeight: 700, marginTop: 2 }}>+{hidden}</span>}
    </span>
  );

  if (chairs <= ROUND_MAX) {
    const diameter = 50 + chairs * 4;
    const radius = diameter / 2 + GAP + CHAIR / 2;
    const box = radius * 2 + CHAIR;
    const center = box / 2;
    return (
      <div style={{ position: "relative", width: box, height: box }}>
        {Array.from({ length: chairs }, (_, i) => {
          const angle = (2 * Math.PI * i) / chairs - Math.PI / 2;
          return (
            <Chair key={i} filled={filled(i)} accent={accent} style={{
              position: "absolute",
              left: center + radius * Math.cos(angle) - CHAIR / 2,
              top: center + radius * Math.sin(angle) - CHAIR / 2,
            }} />
          );
        })}
        <div style={{
          position: "absolute", left: center - diameter / 2, top: center - diameter / 2,
          width: diameter, height: diameter, borderRadius: "50%",
          background: surface, border: `2px solid ${edge}`, boxSizing: "border-box",
          display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center",
        }}>
          {tableLabel}
        </div>
      </div>
    );
  }

  // Dikdörtgen masa: iki uçta birer sandalye, kalanlar uzun kenarlara bölünür.
  // Doldurma sırası saat yönünde: üst kenar → sağ uç → alt kenar → sol uç.
  const sides = chairs - 2;
  const top = Math.ceil(sides / 2);
  const bottom = sides - top;
  const tableWidth = top * STEP + 8;
  const rightIndex = top;
  const leftIndex = top + 1 + bottom;

  const row = (count: number, startIndex: number, reverse: boolean) => (
    <div style={{ display: "flex", justifyContent: "center", gap: STEP - CHAIR, width: tableWidth }}>
      {Array.from({ length: count }, (_, i) => {
        const index = startIndex + (reverse ? count - 1 - i : i);
        return <Chair key={i} filled={filled(index)} accent={accent} />;
      })}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: GAP }}>
      {row(top, 0, false)}
      <div style={{ display: "flex", alignItems: "center", gap: GAP }}>
        <Chair filled={filled(leftIndex)} accent={accent} />
        <div style={{
          width: tableWidth, height: 54, borderRadius: 10,
          background: surface, border: `2px solid ${edge}`, boxSizing: "border-box",
          display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center",
        }}>
          {tableLabel}
        </div>
        <Chair filled={filled(rightIndex)} accent={accent} />
      </div>
      {row(bottom, rightIndex + 1, true)}
    </div>
  );
}
